import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { shortDateFormat } from "src/app/shared/dateTimeHelpers";
import { IndicatorType } from "src/app/shared/models/indicator-types";
import { Language } from "src/app/shared/models/language";
import {
  QuestionType,
  Survey,
  TenantSurvey,
  SurveyAnswerModel,
  SaveResponse,
} from "src/app/shared/models/survey";
import { NotificationService } from "src/app/shared/services/notification.service";
import { PublicSurveyService } from "src/app/shared/services/public-survey.service";
import { SurveyComponent } from "src/app/shared/survey/components/survey.component";

@Component({
    selector: "app-data-entry-page",
    templateUrl: "./data-entry-page.component.html",
    styleUrls: ["./data-entry-page.component.scss"],
    standalone: false
})
export class DataEntryPageComponent implements OnInit {
  @ViewChild(SurveyComponent) surveyComponentDirective: SurveyComponent;

  administrativeUnitSurveyId: string;
  languageId: string;
  administrativeUnitSurvey: TenantSurvey;
  survey: Survey;

  dateFormat: string = shortDateFormat;

  minSortId: number = 1;
  maxSortId: number = 100;

  questionTypes: QuestionType[];
  lastEdit: Date = new Date();

  modifyQuestion$ = new Subject<SaveResponse>();
  languages: Language[];

  surveyIsDisabled: boolean = false;
  indicatorType: number;

  constructor(
    private publicSurveyService: PublicSurveyService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.administrativeUnitSurveyId = this.route.snapshot.paramMap.get("administrativeUnitSurveyId");
    this.languageId = this.route.snapshot.paramMap.get("languageId");
    this.indicatorType = Number(
      this.route.snapshot.paramMap.get("indicatorType")
    );

    this.questionTypes = await this.publicSurveyService
      .getQuestionTypes()
      .toPromise();
    this.administrativeUnitSurvey = await this.publicSurveyService
      .getAdministrativeUnitSurvey(this.administrativeUnitSurveyId)
      .toPromise();
    this.languages = await this.publicSurveyService
      .getLanguages(this.administrativeUnitSurvey.surveyId)
      .toPromise();

    await this.load();
  }

  async load() {
    let tmp = await this.publicSurveyService
      .get(this.administrativeUnitSurvey.surveyId, this.languageId, this.administrativeUnitSurvey.id)
      .toPromise();

    const result = await this.publicSurveyService
      .getLeaAuMonitoringIndicatorsForAdministrativeUnit(
        this.indicatorType,
        this.administrativeUnitSurvey.administrativeUnitId,
        tmp.schoolYear
      )
      .toPromise();
    this.surveyIsDisabled = result
      ? !(result.isOpen || result.isUnlocked)
      : false;

    this.survey = tmp;

    //15148 - parent survey not editable after it has been submitted
    if (
      this.administrativeUnitSurvey.status === "Survey Complete" &&
      this.indicatorType == IndicatorType.Eight
    ) {
      this.surveyIsDisabled = true;
    }
  }

  async languageChanged(event) {
    this.survey = null;
    this.load();
  }

  async onSaveResponse(answer: SurveyAnswerModel) {
    answer.administrativeUnitSurveyId = this.administrativeUnitSurveyId;

    await this.publicSurveyService
      .saveAnswer(answer)
      .toPromise()
      .then((saved: SurveyAnswerModel) => {
        var mod = {
          questionId: saved.questionId,
          success: true,
          errorMsg: null,
        } as SaveResponse;
        this.modifyQuestion$.next(mod);
        this.notificationService.success("Answer has been saved!");
      })
      .catch((err) => {
        var mod = {
          questionId: answer.questionId,
          success: false,
          errorMsg: "Unable to save your answer, please try again.",
        } as SaveResponse;
        this.modifyQuestion$.next(mod);
        this.notificationService.alert(
          "There was a problem saving your response. The information displayed on your screen may not be accurate. Please click 'OK' to reload the current page.",
          "Application Error",
          () => {
            //this.load();
          }
        );
      });
  }

  onSurveyComplete() {
    const questionsToClear =
      this.surveyComponentDirective.getQuestionsToClear();

    this.publicSurveyService
      .submitAdministrativeUnitSurvey(
        this.indicatorType,
        this.administrativeUnitSurvey.id,
        "Survey Complete",
        null,
        questionsToClear
      )
      .subscribe({
        complete: () => {
          this.notificationService.success("Survey Submitted");
          this.router.navigate(["public", "surveys", "thankyou"]);
        },
        error: (err) => {
          this.notificationService.alert(err?.error?.html, err?.error?.title);
        },
      });
  }

  get surveyIsValid() {
    return this.surveyComponentDirective
      ? this.surveyComponentDirective.formIsValid
      : false;
  }

  onStatusChanged() {}
}
